<template>
  <main class="timers" v-show="show">
    <!-- Title -->
    <p>COUNTDOWN TIMER</p>

    <!-- Analog clock -->
    <svg class="analog" v-bind:class="{ animate: stop }" viewBox="0 0 40 40">
      <!-- Hour marks -->
      <g class="hour-marks">
        <line
          x1="18"
          y1="0"
          x2="16"
          y2="0"
          v-for="key in 12"
          v-bind:key="key"
          v-bind:style="{ transform: getHourMarkTransform(key) }"
        />
      </g>
      <!-- Primary black circle -->
      <circle cx="20" cy="20" r="18" />
      <!-- Secondary white circle -->
      <circle
        class="progress"
        cx="20"
        cy="20"
        r="18"
        pathLength="100"
        stroke-dasharray="100"
        v-bind:stroke-dashoffset="100 - analogClockProgress"
        v-on:transitionend="isTimerUp"
      />
      <!-- Ticking hand -->
      <g
        class="hand"
        fill="yellow"
        stroke="yellow"
        v-bind:style="{
          transform: analogHandTransform,
        }"
      >
        <!-- Line of the hand -->
        <line x1="0" y1="0" x2="17" y2="0" />
        <!-- Dot of the hand -->
        <circle cx="18" cy="0" r="1" />
      </g>
    </svg>

    <!-- Digital clock -->
    <p class="digital">
      <span>{{ hoursToDisplayDigital }}<small>h</small></span>
      <span> : {{ minutesToDisplayDigital }}<small>m</small> : </span>
      <span>{{ secondsToDisplayDigital }}<small>s</small></span>
    </p>
  </main>
</template>

<script>
import Timer from "tiny-timer";

export default {
  name: "RbGoogTimer",
  props: {
    hh: String,
    mm: String,
    ss: String,
    show: Boolean,
    pause: Boolean,
    resume: Boolean,
    reset: Boolean,
    start: Boolean,
    stop: Boolean,
  },
  data() {
    const timerDriver = null;
    const hours = 0;
    const minutes = 0;
    const seconds = 0;
    const countDownTimeInMilliSeconds = 0;
    const analogHandRotationDegrees = -90; // Since rotate(360deg) and rotate(0deg) are identical
    const analogClockProgress = 0;
    return {
      timerDriver,
      hours,
      minutes,
      seconds,
      countDownTimeInMilliSeconds,
      analogHandRotationDegrees,
      analogClockProgress,
    };
  },
  computed: {
    hoursToDisplayDigital() {
      return String(this.hours).padStart(2, "0");
    },
    minutesToDisplayDigital() {
      return String(this.minutes).padStart(2, "0");
    },
    secondsToDisplayDigital() {
      return String(this.seconds).padStart(2, "0");
    },
    digitalTimeDisplayString() {
      return `${this.hoursToDisplayDigital}h:${this.minutesToDisplayDigital}m:${this.secondsToDisplayDigital}s`;
    },
    analogHandTransform() {
      return `translate(20px, 20px) rotate(${this.analogHandRotationDegrees}deg)`;
    },
    timerDriverStatus() {
      if (this.timerDriver !== null) {
        return this.timerDriver.status;
      }
      return "notstarted";
    },
  },
  watch: {
    show(inValue) {
      if (inValue === true) {
        this.calculateTimeFromTimeDigits();
        this.startTimers();
      }
    },
    pause(inValue) {
      if (inValue === true) {
        if (this.timerDriver !== null && this.timerDriverStatus === "running") {
          this.timerDriver.pause();
        }
      }
    },
    resume(inValue) {
      if (inValue === true) {
        if (this.timerDriver !== null && this.timerDriverStatus === "paused") {
          this.timerDriver.resume();
        }
      }
    },
    reset(inValue) {
      if (inValue === true) {
        if (
          this.timerDriver !== null &&
          ["running", "paused"].includes(this.timerDriverStatus)
        ) {
          this.calculateTimeFromTimeDigits();
          this.timerDriver.stop();
          this.setAnalogClockProgressZero();
          this.destroyTimerDriverInstance();
        }
      }
    },
    start(inValue) {
      if (inValue === true) {
        this.startTimers();
      }
    },
    stop(inValue) {
      if (inValue === true) {
        if (this.timerDriver !== null && this.timerDriverStatus === "stopped") {
          this.timerDriver.stop();
          this.setAnalogClockProgressComplete();
        }
      }
    },
    digitalTimeDisplayString(inValue) {
      document.title = `${inValue} - COUNTDOWN TIMER`;
    },
  },
  methods: {
    isTimerUp(inEvent) {
      if (
        inEvent.propertyName === "stroke-dashoffset" &&
        this.timerDriverStatus === "stopped" &&
        this.reset !== true
      ) {
        this.$emit("timerUp");
        if (navigator && typeof navigator.vibrate === "function") {
          navigator.vibrate([300, 10, 300, 10]);
        }
      }
    },
    isOverSixty(inMeasure) {
      return inMeasure >= 60 ? true : false;
    },
    getHourMarkTransform(inKey) {
      return `rotate(calc(${inKey * 30}deg))`;
    },
    getCountDownTimeInMilliSeconds() {
      const oneSecondInMilliSeconds = 1000;
      const oneMinuteInMilliSeconds = 60 * oneSecondInMilliSeconds;
      const oneHourInMilliSeconds = 60 * oneMinuteInMilliSeconds;
      const setHoursInMilliSeconds = this.hours * oneHourInMilliSeconds;
      const setMinutesInMilliSeconds = this.minutes * oneMinuteInMilliSeconds;
      const setSecondsInMilliSeconds = this.seconds * oneSecondInMilliSeconds;
      return (
        setHoursInMilliSeconds +
        setMinutesInMilliSeconds +
        setSecondsInMilliSeconds
      );
    },
    setAnalogClockProgress(inTimeLeft) {
      const totalCountDownMilliSeconds = this.countDownTimeInMilliSeconds;
      const progressInAngles =
        ((totalCountDownMilliSeconds - inTimeLeft) * 360) /
        totalCountDownMilliSeconds;
      const percentageOfProgressInAngles = (progressInAngles * 100) / 360;
      let percentageToShowForStatus;
      switch (this.timerDriverStatus) {
        case "paused":
        case "running":
          percentageToShowForStatus = percentageOfProgressInAngles;
          break;
        case "notstarted":
        case "stopped":
          percentageToShowForStatus = 100;
          break;
        default:
          percentageToShowForStatus = percentageOfProgressInAngles;
      }
      this.analogClockProgress = percentageToShowForStatus;
      if (inTimeLeft <= 0 && this.timerDriverStatus === "stopped") {
        this.$emit("timerAboutToEnd");
      }
    },
    setAnalogClockProgressZero() {
      this.analogHandRotationDegrees = -90;
      this.analogClockProgress = 0;
    },
    setAnalogClockProgressComplete() {
      this.analogHandRotationDegrees = 270; // since rotate(0deg) and rotate(360deg) are same
      this.analogClockProgress = 100;
    },
    calculateTimeFromTimeDigits() {
      const setSeconds = Number(this.ss);

      this.seconds = Number(this.ss) % 60;
      this.minutes = Number(this.mm);
      this.hours = Number(this.hh);
      if (this.isOverSixty(setSeconds)) {
        this.minutes = this.minutes + 1;
        if (this.isOverSixty(this.minutes)) {
          this.minutes = this.minutes % 60;
          this.hours = this.hours + 1;
        }
      } else if (this.isOverSixty(this.minutes)) {
        this.minutes = this.minutes % 60;
        this.hours = this.hours + 1;
      }
    },
    tickAnalogTimer(inMilliSecond) {
      const totalCountDownMilliSeconds = this.countDownTimeInMilliSeconds;
      const totalDegreesOfClock = 360;
      if (totalCountDownMilliSeconds === inMilliSecond) {
        return;
      }
      const stepTraversalInterval = 1000;
      const totalStepsToCompleteTheAnalogTraversal =
        totalCountDownMilliSeconds / stepTraversalInterval;
      const angleJumps =
        totalDegreesOfClock / totalStepsToCompleteTheAnalogTraversal;
      this.setAnalogClockProgress(inMilliSecond);
      this.analogHandRotationDegrees += angleJumps;
    },
    tickDigitalTimer(inMilliSecond) {
      this.seconds = Math.ceil((inMilliSecond / 1000) % 60);
      this.minutes = Math.trunc((inMilliSecond / (1000 * 60)) % 60);
      this.hours = Math.trunc(inMilliSecond / (1000 * 60 * 60));
    },
    startTimers() {
      this.setAnalogClockProgressZero();

      if (this.timerDriver !== null) {
        this.destroyTimerDriverInstance();
      }

      this.timerDriver = new Timer();

      this.timerDriver.on("tick", (ms) => {
        this.tickDigitalTimer(ms);
        this.tickAnalogTimer(ms);
      });

      this.countDownTimeInMilliSeconds = this.getCountDownTimeInMilliSeconds();

      this.timerDriver.start(this.countDownTimeInMilliSeconds);
    },
    destroyTimerDriverInstance() {
      this.timerDriver = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.timers {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;

  .analog {
    inline-size: var(--size-15);
    fill: transparent;
    stroke: var(--gray-9);
    stroke-width: 2;
    stroke-linecap: round;

    &.animate {
      animation: var(--animation-pulse);
    }

    .hour-marks {
      transform: translate(20px, 20px);
      stroke: var(--gray-9);
      stroke-width: 1;
    }

    > circle {
      transform: translate(0px, 40px) rotate(-90deg);

      &.progress {
        stroke: var(--gray-0);
        transition-property: stroke-dashoffset;
        transition-duration: 1s;
      }
    }

    .hand {
      transition-property: transform;
      transition-duration: 1s;
    }
  }

  .digital {
    font-size: clamp(1.5rem, 9vw, 3.5rem);
    font-family: var(--font-mono);
    font-weight: var(--font-weight-9);
    color: var(--gray-0);
    font-feature-settings: "tnum";
    font-variant-numeric: tabular-nums;

    & small {
      color: var(--gray-5);
    }
  }
}
</style>
